import React, { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight, oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { BiCheck } from "react-icons/bi";
import { MdOutlineCopyAll } from "react-icons/md";
import { TbCode } from "react-icons/tb";
import { BsThreeDotsVertical } from "react-icons/bs";

import "katex/dist/katex.min.css";

const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);
    const copyToClipboard = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="code_cont_header">
            <i><TbCode /></i>
            <div className="copy_markdown_code_cont">
                <button onClick={copyToClipboard} className="copy_markdown_code_btn">
                    {copied ? <BiCheck /> : <MdOutlineCopyAll />}
                </button>
                <button className="copy_markdown_code_btn">
                    <BsThreeDotsVertical />
                </button>
            </div>
        </div>
    );
};

const Markdown = ({ msg, chatEndRef, userLastMessageRef, sources = [] }) => {
    const rawText = typeof msg === "string" ? msg : msg?.content || msg?.text || "";

    const processedText = useMemo(() => {
        if (!rawText) return "";

        let text = rawText;

        text = text.replace(/【source\s*(\d+)】/gi, (match, number) => {
            const index = parseInt(number);
            const source = sources[index];
            return source && source.link ? `[[ ${number} ]](${source.link})` : match;
        });

        text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, equation) => {
            return `\n$$ \n${equation.trim()}\n $$\n`;
        });

        text = text.replace(/\\\(([\s\S]*?)\\\)/g, (_, equation) => {
            return `$ ${equation.trim()} $`;
        });

        text = text.replace(/\$(?=\d)/g, '\\$');

        return text;
    }, [rawText, sources]);

    useEffect(() => {
        if (!isAtBottom) return;

        const id = requestAnimationFrame(() => {
            // chatEndRef.current?.scrollIntoView({ behavior: "auto" });
            // userLastMessageRef.current?.scrollIntoView({ behavior: "auto" });
        });

        return () => cancelAnimationFrame(id);
    }, [processedText]);

    const [isAtBottom, setIsAtBottom] = useState(true);

    const handleScroll = (e) => {
        const el = e.target;
        const threshold = 80;

        const atBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

        setIsAtBottom(atBottom);
    };

    if (!processedText) return null;

    const cleanedTheme = {
        ...oneDark,
        'pre[class*="language-"]': {
            ...oneLight['pre[class*="language-"]'],
            background: "transparent",
            backgroundColor: "transparent",
            color: "var(--originalBlack)"
        },
        'code[class*="language-"]': {
            ...oneDark['code[class*="language-"]'],
            background: "transparent",
            backgroundColor: "transparent",
            color: "var(--originalBlack)"
        }
    };

    return (
        <div className="markdown-container fade-in" style={{ width: '100%' }} onScroll={handleScroll}>
            <style dangerouslySetInnerHTML={{
                __html: `
                .katex-mathml { display: none !important; }
                .katex-html { display: inline-block; }
                .katex-display { 
                    margin: 1.5em 0; 
                    overflow-x: auto; 
                    overflow-y: hidden;
                    text-align: center;
                }
            `}} />

            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[[rehypeKatex, { output: 'html', throwOnError: false }]]}
                components={{
                    hr: () => null,
                    h1: ({ node, ...props }) => <h1 style={{ fontSize: "20px", color: "var(--originalBlack)" }} {...props} />,
                    h2: ({ node, ...props }) => <h2 style={{ fontSize: "18px", color: "var(--originalBlack)" }} {...props} />,
                    h3: ({ node, ...props }) => <h3 style={{ fontSize: "16px", color: "var(--originalBlack)" }} {...props} />,
                    p: ({ node, children, ...props }) => (
                        <p style={{ fontSize: "14px", color: "var(--originalBlack)", lineHeight: "1.6", marginBottom: "10px" }} {...props}>
                            {children}
                        </p>
                    ),
                    a: ({ node, children, href, ...props }) => {
                        const isCitation = Array.isArray(children) && typeof children[0] === 'string' && children[0].startsWith('[');
                        return (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: "none",
                                    fontSize: isCitation ? "0.85em" : "inherit",
                                    marginLeft: "2px",
                                    wordBreak: "break-word",
                                }}
                                {...props}
                            >
                                {children}
                            </a>
                        );
                    },
                    table: ({ node, ...props }) => (
                        <div style={{ overflowX: "auto", margin: "15px 0" }}>
                            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "14px", border: "1px solid var(--milkyWhite)" }} {...props} />
                        </div>
                    ),
                    thead: ({ node, ...props }) => <thead style={{ backgroundColor: "var(--milkyWhite)" }} {...props} />,
                    th: ({ node, ...props }) => (
                        <th style={{ border: "1px solid var(--milkyWhite)", padding: "10px", textAlign: "left", fontWeight: "bold" }} {...props} />
                    ),
                    td: ({ node, ...props }) => (
                        <td style={{ border: "1px solid var(--milkyWhite)", padding: "10px" }} {...props} />
                    ),
                    code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        const codeString = String(children).replace(/\n$/, "");

                        if (!inline && match) {
                            return (
                                <div className="code_block_syntax">
                                    <CopyButton code={codeString} />
                                    <SyntaxHighlighter
                                        style={cleanedTheme}
                                        language={match[1]}
                                        PreTag="div"
                                        className="code_block_syntax_item"
                                        {...props}
                                    >
                                        {codeString}
                                    </SyntaxHighlighter>
                                </div>
                            );
                        }

                        return (
                            <code
                                className={className}
                                {...props}
                            >
                                {children}
                            </code>
                        );
                    },
                }}
            >
                {processedText}
            </ReactMarkdown>
        </div>
    );
};

export default Markdown;